import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { FeedbackIcon } from "../../assets/icon";
import { feedbackButtonContainerSx, feedbackIconButtonSx } from "./styles";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../../utils/internetConnection";
import { useAppStore } from "../../stores/appStore";

interface FeedbackSupportButtonProps {
  bottom?: number | string;
  right?: number | string;
  position?: "fixed" | "absolute" | "static";
  onClick?: () => void;
  tooltipTitle?: string;
  disabled?: boolean;
  forceLargeSize?: boolean;
}

export const FeedbackSupportButton: React.FC<FeedbackSupportButtonProps> = ({
  bottom = 24,
  right = 24,
  position = "fixed",
  onClick,
  tooltipTitle = "Need help?",
  disabled = false,
  forceLargeSize = false,
}) => {
  const supportEmail = "support@4bridge.com";
  const isOnline = useAppStore((s) => s.isOnline);

  const buildFeedbackSubject = () => "CoachMate feedback";

  const buildFeedbackBody = () =>
    ["Hi,", "", "I would like to share feedback:", "", "Message:"].join("\n");

  const handleClick = () => {
    if (disabled) return;
    if (!isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }

    if (onClick) {
      onClick();
      return;
    }

    const subject = buildFeedbackSubject();
    const body = buildFeedbackBody();

    const mailtoUrl = `mailto:${encodeURIComponent(supportEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const gmailComposeUrl = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=${encodeURIComponent(supportEmail)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoUrl;

    // Fallback for systems where mailto handler is not configured.
    window.setTimeout(() => {
      if (document.visibilityState !== "visible") return;

      const shouldOpenGmail = window.confirm(
        "Could not open your email app automatically.\n\nPress OK to open Gmail compose.",
      );

      if (shouldOpenGmail) {
        window.location.assign(gmailComposeUrl);
      }
    }, 900);
  };

  return (
    <Box sx={feedbackButtonContainerSx(right, bottom, position)}>
      <Tooltip
        title={tooltipTitle}
        placement="top"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#FFFFFF",
              color: "#06B6D4",
              border: "1px solid #E6DEFF",
              fontWeight: 700,
              fontSize: "12px",
              boxShadow: "0 6px 20px rgba(34, 24, 79, 0.15)",
              mr: "5px",
            },
          },
          arrow: {
            sx: {
              color: "#FFFFFF",
              "&::before": {
                border: "1px solid #E6DEFF",
              },
            },
          },
        }}
      >
        <IconButton
          onClick={handleClick}
          aria-label="Open feedback"
          disabled={disabled}
          sx={feedbackIconButtonSx(forceLargeSize)}
        >
          <FeedbackIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
